import Phaser from 'phaser';
import { HAPTICS, PALETTE } from '../../config';
import { createAthleteView, type AthleteView } from '../../game/AthleteView';
import { AudioEngine } from '../../game/AudioEngine';
import { getDiscipline } from '../../game/data/disciplines';
import { getSkin } from '../../game/data/skins';
import { pickOpponents } from '../../game/data/aiRoster';
import { t } from '../../game/i18n';
import type { DisciplineResult, Standing } from '../../game/types';
import { Button } from '../../ui/Button';
import { PowerMeter } from '../../ui/PowerMeter';
import { viewport } from '../../ui/viewport';
import { DisciplineScene } from './DisciplineScene';

type Side = 'L' | 'R';
type Phase = 'intro' | 'board' | 'air' | 'done';

const GRAVITY = 900; // px/s^2

/**
 * Diving — stop the power bar to spring off the platform, then mash SX/DX to SPIN
 * (somersaults) on the way down, and tap ENTRA just before the water while VERTICAL for a
 * clean entry. Judges score the dive 0–100 (height + flips + entry). Beat your best for a record.
 */
export class Diving extends DisciplineScene {
  private phase: Phase = 'intro';
  private rig!: AthleteView;

  private power = 0;
  private vy = 0;
  private y = 0;
  private rot = 0;
  private rotSpeed = 0;
  private lastSide: Side | null = null;
  private halfTurns = 0;
  private entered = false;
  private entryQ = 0;
  private heightQ = 0;
  private platformY = 0;
  private waterY = 0;
  private diverX = 0;

  private W = 0; private H = 0; private uiS = 1;
  private aiScores: { name: string; species: string; nationality: string; metric: number }[] = [];

  private powerBar!: PowerMeter;
  private hud!: Phaser.GameObjects.Text;
  private bigText!: Phaser.GameObjects.Text;
  private actBtn!: Button;
  private turnsText!: Phaser.GameObjects.Text;

  constructor() { super('Diving'); }

  create(): void {
    super.create();
    this.phase = 'intro';
    this.power = 0; this.vy = 0; this.rot = 0; this.rotSpeed = 0; this.lastSide = null; this.halfTurns = 0; this.entered = false; this.entryQ = 0; this.heightQ = 0;

    const { w, h, s } = viewport(this);
    this.W = w; this.H = h; this.uiS = s;
    this.platformY = h * 0.26;
    this.waterY = h * 0.82;
    this.diverX = w * 0.32;
    this.y = this.platformY;

    this.drawScene();
    this.rig = createAthleteView(this, this.diverX, this.platformY, this.athlete, Phaser.Math.Clamp(h * 0.2, 100, 180));
    this.rig.container.setDepth(6);

    const opponents = pickOpponents(getSkin(this.athlete.skin).id, 3);
    this.aiScores = opponents.map((o) => ({ name: o.name, species: o.species, nationality: o.nationality, metric: Math.round(Math.max(35, 55 + (o.jumpDistance - 5) * 5 + (Math.random() * 24 - 12))) }));

    this.buildHud();
    this.controls.on('tapLeft', () => this.onTap('L'));
    this.controls.on('tapRight', () => this.onTap('R'));
    this.controls.on('action', () => this.onAction());
    this.startCountdown();
  }

  private drawScene(): void {
    const g = this.add.graphics().setDepth(-10);
    g.fillStyle(0x12306a, 1).fillRect(0, 0, this.W, this.H);
    g.fillStyle(0x1f6fb2, 1).fillRect(0, this.waterY, this.W, this.H - this.waterY); // pool
    g.fillStyle(0x9fd3ff, 0.25).fillRect(0, this.waterY, this.W, 4);
    // Platform/tower.
    g.fillStyle(0xced6e6, 1).fillRect(this.diverX - 60, this.platformY + 20, 80, 14);
    g.fillStyle(0x6b7390, 1).fillRect(this.diverX - 26, this.platformY + 34, 16, this.waterY - this.platformY - 34);
  }

  private buildHud(): void {
    const fs = (n: number) => `${Math.max(13, Math.round(n * this.uiS))}px`;
    this.hud = this.add.text(this.W / 2, 16, t('game.hudPush'), { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: fs(24), color: '#f4f7ff', fontStyle: 'bold' })
      .setOrigin(0.5, 0).setScrollFactor(0).setShadow(0, 3, '#000000aa', 5);
    const barW = Math.min(300, this.W * 0.5);
    const topY = Math.max(64, this.H * 0.16);
    this.powerBar = new PowerMeter(this, this.W / 2, topY, { width: barW, height: 18, fillColor: PALETTE.power });
    this.add.text(this.W / 2, topY - 18, t('hud.push'), { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: fs(12), color: '#d3dbf2' }).setOrigin(0.5).setScrollFactor(0);
    this.turnsText = this.add.text(this.W / 2, topY + 22, '', { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: fs(18), color: '#ffd24a', fontStyle: 'bold' }).setOrigin(0.5).setScrollFactor(0);
    this.bigText = this.add.text(this.W / 2, this.H * 0.5, '', { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: fs(72), color: '#ffd24a', fontStyle: 'bold' })
      .setOrigin(0.5).setScrollFactor(0).setShadow(0, 8, '#00000088', 12);
    const label = this.sys.game.device.os.desktop ? `💦 ${t('btn.push')} ${t('btn.space')}` : `💦 ${t('btn.push')}`;
    this.actBtn = new Button(this, this.W / 2, this.H - Math.max(30, 38 * this.uiS), label, () => this.onAction(), {
      width: Math.min(200, this.W * 0.4), height: Math.max(52, 58 * this.uiS), fontSize: Math.max(18, Math.round(22 * this.uiS)), color: PALETTE.power,
    });
    this.actBtn.setScrollFactor(0).setAlpha(0.92).setVisible(false);
  }

  private startCountdown(): void {
    ['3', '2', '1', t('game.go')].forEach((label, i) => {
      this.time.delayedCall(i * 600, () => {
        this.bigText.setText(label).setAlpha(1).setScale(1.5);
        this.tweens.add({ targets: this.bigText, scale: 1, duration: 300, ease: 'Back.out' });
        if (label === t('game.go')) {
          AudioEngine.go();
          this.phase = 'board';
          this.controls.setEnabled(true);
          this.actBtn.setVisible(true);
          this.powerBar.setVisible(true);
          this.hud.setText(t('game.stopPush'));
          this.tweens.add({ targets: this.bigText, alpha: 0, delay: 250, duration: 200 });
        } else AudioEngine.countdownTick();
      });
    });
  }

  private onAction(): void {
    if (this.phase === 'board') {
      this.heightQ = this.power;
      this.vy = -(420 + this.power * 320); // launch upward
      this.phase = 'air';
      this.powerBar.setVisible(false);
      this.actBtn.setText(this.sys.game.device.os.desktop ? `🎯 ${t('btn.enter')} ${t('btn.space')}` : `🎯 ${t('btn.enter')}`);
      this.hud.setText(t('game.spinEnter'));
      this.rig.setMotion(0);
      AudioEngine.go();
      this.vibrate(HAPTICS.goodTap);
    } else if (this.phase === 'air' && !this.entered && this.y > this.waterY - this.H * 0.32) {
      // Lock the entry: quality = how vertical you are AND how close to the water (timing).
      this.entered = true;
      const closeness = Phaser.Math.Clamp((this.y - (this.waterY - this.H * 0.32)) / (this.H * 0.32), 0, 1);
      this.entryQ = Math.abs(Math.cos(this.rot)) * (0.55 + 0.45 * closeness);
      this.rotSpeed = 0;
      this.rot = Math.round(this.rot / Math.PI) * Math.PI; // snap to vertical
      this.hud.setText(t('game.entry'));
      this.vibrate(HAPTICS.goodTap);
    }
  }

  private onTap(side: Side): void {
    if (this.phase !== 'air' || this.entered) return;
    const alternated = side !== this.lastSide;
    this.lastSide = side;
    this.rotSpeed = Math.min(14, this.rotSpeed + (alternated ? 2.2 : 0.8));
    AudioEngine.tap(alternated ? 0.5 : 0.2);
    this.vibrate(HAPTICS.tap);
  }

  update(_t: number, delta: number): void {
    const dt = delta / 1000;
    this.rig.update(dt);
    if (this.phase === 'board') {
      this.power = 0.5 + 0.5 * Math.sin(this.time.now / 1000 * 3.4 - Math.PI / 2);
      this.powerBar.set(this.power);
    } else if (this.phase === 'air') {
      this.updateAir(dt);
    }
  }

  private updateAir(dt: number): void {
    this.vy += GRAVITY * dt;
    this.y += this.vy * dt;
    if (!this.entered) this.rot += this.rotSpeed * dt;
    this.halfTurns = Math.floor(Math.abs(this.rot) / Math.PI);
    this.rig.container.y = this.y;
    this.rig.container.x = this.diverX + (this.y - this.platformY) * 0.12; // slight forward travel
    this.rig.container.setRotation(this.rot);
    this.turnsText.setText(t('game.spins', { n: (Math.abs(this.rot) / (Math.PI * 2)).toFixed(1) }));
    if (this.y >= this.waterY) this.enterWater();
  }

  private enterWater(): void {
    this.phase = 'done';
    this.controls.setEnabled(false);
    if (!this.entered) this.entryQ = Math.abs(Math.cos(this.rot)); // never tapped → judge as-is
    this.rig.container.setRotation(0);
    this.rig.container.setVisible(false);
    this.cameras.main.shake(120, 0.004);
    AudioEngine.thud();
    this.add.particles(this.rig.container.x, this.waterY, 'spark', {
      lifespan: 700, speed: { min: 60, max: 220 }, angle: { min: 200, max: 340 },
      scale: { start: 0.7, end: 0 }, alpha: { start: 0.7, end: 0 }, tint: 0xbfe6ff, gravityY: 500, emitting: false,
    }).setDepth(8).explode(26);
    this.vibrate(HAPTICS.finish);

    // Entry quality dominates and is squared (an imperfect entry hurts a lot); flips give a
    // modest bonus. 100 needs a near-perfect, well-spun, clean dive — so records stay contestable.
    const flips = Math.min(this.halfTurns, 4);
    const entry = this.entryQ * this.entryQ;
    const score100 = Math.round(Phaser.Math.Clamp(5 + flips * 7 + entry * 55 + this.heightQ * 12, 0, 100));

    const standings: Standing[] = [
      { name: this.athlete.name, species: this.athlete.species, isPlayer: true, nationality: this.athlete.nationality, metric: score100 },
      ...this.aiScores.map((a) => ({ name: a.name, species: a.species, isPlayer: false, nationality: a.nationality, metric: a.metric })),
    ].sort((x, y) => y.metric - x.metric);
    const rank = standings.findIndex((s) => s.isPlayer) + 1;
    const score = Math.round(Phaser.Math.Clamp(score100 * 10, 0, 1000));
    const reward = getDiscipline('diving').scoreToReward(score);

    const grade = this.entryQ > 0.85 ? t('game.diveClean') : this.entryQ > 0.5 ? t('game.diveGood') : t('game.diveSplash');
    this.hud.setText(grade);
    this.bigText.setText(`${score100} pt`).setColor(this.entryQ > 0.5 ? '#ffd24a' : '#ff5d6c').setAlpha(1).setScale(0.6);
    this.tweens.add({ targets: this.bigText, scale: 1, duration: 500, ease: 'Back.out' });
    AudioEngine.fanfare();

    const result: DisciplineResult = {
      disciplineId: 'diving', metricKind: 'distance', athleteId: this.athlete.id, athleteName: this.athlete.name,
      species: this.athlete.species, nationality: this.athlete.nationality, metric: score100, score, reward, rank, standings,
    };
    this.time.delayedCall(2400, () => this.finishDiscipline(result));
  }

  private vibrate(pattern: number | number[]): void {
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      try { navigator.vibrate(pattern); } catch { /* unsupported */ }
    }
  }
}
